/**
 * Which fulfilment routes a customer catalogue, or one catalogue item, may use
 * — third-party fulfilment, phase 2 (docs/features/catalogue-third-party-
 * fulfilment/07_phase_2_product_brief.md, #87, journey F).
 *
 * The same moves the catalogue already has for options — allow a subset, or
 * pin to one — applied to fulfilment routes. Narrowing only removes: which
 * routes can exist at all is the dictionary item's mappings (a code change),
 * not something set here. An item narrows within its catalogue.
 *
 * What a narrowing means for ordering — options no allowed route can make,
 * and that a single-supplier pin stops the item being bought while that
 * supplier cannot quote — is the backend's to say
 * (the command's dry run); the input passes the lines
 * in as `consequences`.
 *
 * The Speeds are narrowed the same way (d63): every Speed, or these Speeds
 * (one fixes it). The Speeds are Time's ladder, read from the backend; the
 * editor knows none of its own.
 */
import { useId, useState } from "react";
import type { RouteNarrowing } from "@shared-core/data/supplier-pricing-types";
import { IN_HOUSE_ROUTE } from "@shared-core/data/supplier-pricing-types";
import type { SpeedLadderRung } from "@shared-core/data/speed-types";
import { describeSpeedNarrowing } from "@shared-core/domain/speed-presentation";
import { describeRouteNarrowing } from "@shared-core/domain/supplier-pricing-presentation";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Checkbox } from "../../primitives/checkbox";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";

export interface RouteChoice {
  routeCode: string;
  routeLabel: string;
  /** Shown beside the route, e.g. "Suspended". */
  note?: string | null;
}

type Mode = "every" | "pinned" | "subset";

function modeOf(n: RouteNarrowing): Mode {
  if (n.pinnedRoute) return "pinned";
  if (n.allowedRoutes?.length) return "subset";
  return "every";
}

export function RouteNarrowingEditor({
  scope,
  routes,
  narrowing,
  onChange,
  inherited,
  consequences = [],
  speeds,
}: {
  scope: "catalogue" | "item";
  /** In-house first, then every supplier route. */
  routes: RouteChoice[];
  narrowing: RouteNarrowing;
  onChange: (next: RouteNarrowing) => void;
  /** For an item: what its catalogue already allows, in words. */
  inherited?: string | null;
  /** What this narrowing would mean for ordering, as the backend words it. */
  consequences?: ReadonlyArray<string>;
  /** Time's Speed ladder, slowest first; absent, the Speeds are not edited here. */
  speeds?: ReadonlyArray<SpeedLadderRung>;
}) {
  const idPrefix = useId();
  // Held locally so "These routes" with every box cleared stays selected and
  // can say what is wrong, rather than snapping back to "every route".
  const [mode, setMode] = useState<Mode>(() => modeOf(narrowing));
  const labelFor = (code: string) =>
    routes.find((r) => r.routeCode === code)?.routeLabel ?? code;
  const pinned = narrowing.pinnedRoute ?? null;
  const allowed = narrowing.allowedRoutes ?? [];
  const noun = scope === "catalogue" ? "items in this catalogue" : "this item";

  // A route change keeps the Speed narrowing, and a Speed change the routes.
  const speedPart =
    narrowing.allowedSpeeds != null
      ? { allowedSpeeds: narrowing.allowedSpeeds }
      : {};
  const routePart = {
    ...(narrowing.pinnedRoute !== undefined
      ? { pinnedRoute: narrowing.pinnedRoute }
      : {}),
    ...(narrowing.allowedRoutes !== undefined
      ? { allowedRoutes: narrowing.allowedRoutes }
      : {}),
  };
  const changeRoutes = (next: RouteNarrowing) =>
    onChange({ ...next, ...speedPart });
  const chooseMode = (next: Mode) => {
    setMode(next);
    if (next === "every") changeRoutes({});
    if (next === "pinned")
      changeRoutes({
        pinnedRoute: IN_HOUSE_ROUTE,
        allowedRoutes: [IN_HOUSE_ROUTE],
      });
    if (next === "subset")
      changeRoutes({
        pinnedRoute: null,
        allowedRoutes: routes.map((r) => r.routeCode),
      });
  };
  const allowedSpeeds = narrowing.allowedSpeeds ?? null;
  const changeSpeeds = (next: string[] | null) =>
    onChange({
      ...routePart,
      ...(next != null ? { allowedSpeeds: next } : {}),
    });

  return (
    <fieldset className="space-y-3 text-sm">
      <legend className="font-medium">Fulfilment routes</legend>
      <p className="text-xs text-muted-foreground">
        Who may make {noun}: {describeRouteNarrowing(narrowing, labelFor)}.
        {inherited && ` Within the catalogue's own choice: ${inherited}.`}
      </p>
      <RadioGroup
        aria-label="Fulfilment routes"
        value={mode}
        onValueChange={(v) => chooseMode(v as Mode)}
        className="gap-2"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="every" id={`${idPrefix}-every`} />
          <Label htmlFor={`${idPrefix}-every`} className="font-normal">
            Every route the item can use
          </Label>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <RadioGroupItem value="pinned" id={`${idPrefix}-pinned`} />
          <Label htmlFor={`${idPrefix}-pinned`} className="font-normal">
            Only
          </Label>
          <Select
            disabled={mode !== "pinned"}
            value={pinned ?? IN_HOUSE_ROUTE}
            onValueChange={(code) =>
              changeRoutes({ pinnedRoute: code, allowedRoutes: [code] })
            }
          >
            <SelectTrigger
              size="sm"
              aria-label="The one route allowed"
              className="w-40"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {routes.map((r) => (
                <SelectItem key={r.routeCode} value={r.routeCode}>
                  {r.routeLabel}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <RadioGroupItem value="subset" id={`${idPrefix}-subset`} />
          <Label htmlFor={`${idPrefix}-subset`} className="font-normal">
            These routes:
          </Label>
        </div>
      </RadioGroup>

      {mode === "subset" && (
        <div className="ml-6 space-y-1.5">
          {routes.map((r) => {
            const checked = allowed.includes(r.routeCode);
            const id = `${idPrefix}-route-${r.routeCode}`;
            return (
              <div key={r.routeCode} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={checked}
                  onCheckedChange={() =>
                    changeRoutes({
                      pinnedRoute: null,
                      allowedRoutes: checked
                        ? allowed.filter((c) => c !== r.routeCode)
                        : [...allowed, r.routeCode],
                    })
                  }
                />
                <Label htmlFor={id} className="font-normal">
                  {r.routeLabel}
                  {r.note && (
                    <span className="text-xs text-muted-foreground">
                      ({r.note})
                    </span>
                  )}
                </Label>
              </div>
            );
          })}
          {allowed.length === 0 && (
            <p className="text-xs text-destructive">
              Choose at least one route, or nothing in {noun} can be bought.
            </p>
          )}
        </div>
      )}

      {speeds && speeds.length > 0 && (
        <div className="space-y-2 border-t border-border pt-3">
          <div className="font-medium">Speeds</div>
          <p className="text-xs text-muted-foreground">
            The Speeds {noun} may be ordered at:{" "}
            {describeSpeedNarrowing(allowedSpeeds, speeds)}. One Speed fixes it.
          </p>
          <RadioGroup
            aria-label="Speeds"
            value={allowedSpeeds == null ? "every" : "subset"}
            onValueChange={(v) =>
              changeSpeeds(
                v === "every" ? null : speeds.map((s) => s.speedCode),
              )
            }
            className="gap-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="every" id={`${idPrefix}-speeds-every`} />
              <Label
                htmlFor={`${idPrefix}-speeds-every`}
                className="font-normal"
              >
                Every Speed
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="subset" id={`${idPrefix}-speeds-subset`} />
              <Label
                htmlFor={`${idPrefix}-speeds-subset`}
                className="font-normal"
              >
                These Speeds:
              </Label>
            </div>
          </RadioGroup>
          {allowedSpeeds != null && (
            <div className="ml-6 space-y-1.5">
              {speeds.map((s) => {
                const checked = allowedSpeeds.includes(s.speedCode);
                const id = `${idPrefix}-speed-${s.speedCode}`;
                return (
                  <div key={s.speedCode} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={checked}
                      onCheckedChange={() =>
                        changeSpeeds(
                          checked
                            ? allowedSpeeds.filter((c) => c !== s.speedCode)
                            : speeds
                                .map((x) => x.speedCode)
                                .filter(
                                  (c) =>
                                    allowedSpeeds.includes(c) ||
                                    c === s.speedCode,
                                ),
                        )
                      }
                    />
                    <Label htmlFor={id} className="font-normal">
                      {s.label}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({s.workingDaysToArrival} working day
                        {s.workingDaysToArrival === 1 ? "" : "s"})
                      </span>
                    </Label>
                  </div>
                );
              })}
              {allowedSpeeds.length === 0 && (
                <p className="text-xs text-destructive">
                  Choose at least one Speed, or nothing in {noun} can be
                  ordered.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {consequences.length > 0 && (
        <Alert
          role="status"
          className="border-amber-300 bg-amber-50 px-3 py-2 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
        >
          <AlertDescription className="text-xs text-current">
            <ul className="space-y-0.5">
              {consequences.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </fieldset>
  );
}
